// A compact, always-visible guidance card on the Today screen. Brings the key
// schedule numbers — wake window, feed cadence, day-sleep vs cap, longest gap
// between feeds — out of the buried Plan tab and onto the screen a tired parent
// actually reads. Pure: all values from the deterministic engine, no network.
//
// The fourth tile used to be a "night goal" — an hours target with a red
// "behind the goal" state. It read as a scolding at 4am, so it's now a plain
// statement of the longest stretch she actually went between feeds.

import { useMemo } from "react";
import type { EventRow } from "../lib/events";
import type { AgeBand } from "../data/tcbSchedule";
import { daytimeSleepStatus, longestFeedGap } from "../lib/schedule";
import { durStr, hmShort, minutesFromMidnight } from "../lib/timeFmt";
import { useT } from "../lib/LocaleContext";

type Props = {
  events: EventRow[];
  band: AgeBand;
  now: Date;
};

function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

export function GuidanceStrip({ events, band, now }: Props) {
  // Day sleep is defined by the 7am–7pm clock window, not by sleep_kind. Some
  // rows can carry a stale/incorrect "night" label even though the timeline
  // correctly shows them as daytime naps; normalizing that label here keeps the
  // Today card from silently dropping those naps from the total.
  const ds = useMemo(
    () => daytimeSleepStatus(
      events.map((e) => (e.type === "sleep" ? { ...e, sleep_kind: null } : e)),
      band,
      now,
    ),
    [events, band, now],
  );
  const gap = useMemo(() => longestFeedGap(events, now), [events, now]);
  const t = useT();

  // Time-since-last-wake, but only meaningful within a sane window — a stale
  // last-nap from days ago would otherwise read as "3832m awake".
  const wakeSub = useMemo(() => {
    if (events.some((e) => e.type === "sleep" && e.end_at == null)) return t.guidance.asleepNow;
    const lastWake = events
      .filter((e) => e.type === "sleep" && e.end_at != null)
      .sort((a, b) => new Date(b.end_at!).getTime() - new Date(a.end_at!).getTime())[0];
    if (!lastWake) return t.guidance.noNapsYet;
    const mins = Math.round((now.getTime() - new Date(lastWake.end_at!).getTime()) / 60_000);
    if (mins > 360) return t.guidance.noRecentNap;
    return t.guidance.minsAwake(mins);
  }, [events, t, now]);

  const daySleep =
    ds.capMin != null
      ? `${round1(ds.totalDaytimeMin / 60)}h / ${round1(ds.capMin / 60)}h`
      : ds.totalDaytimeMin > 0
        ? `${round1(ds.totalDaytimeMin / 60)}h`
        : "—";

  return (
    <div style={styles.wrap}>
      <div style={styles.label}>{t.guidance.heading}</div>
      <div style={styles.grid}>
        <Tile
          label={t.guidance.wakeWindow}
          value={`${band.wakeWindowMin}–${band.wakeWindowMax}m`}
          sub={wakeSub}
        />
        <Tile
          label={t.guidance.feeds}
          value={t.guidance.feedEvery(band.feedIntervalHrsMin, band.feedIntervalHrsMax)}
          sub={t.guidance.feedsSub}
        />
        <Tile
          label={t.guidance.daySleep}
          value={daySleep}
          sub={ds.overCap ? t.guidance.pastGuide : ds.capMin != null ? t.guidance.withinGuide : t.guidance.noCapYet}
          alert={ds.overCap}
        />
        <Tile
          label={t.guidance.longestGap}
          value={gap ? durStr(gap.min) : "—"}
          sub={
            gap
              ? t.guidance.gapSpan(
                  hmShort(minutesFromMidnight(gap.startAt)),
                  hmShort(minutesFromMidnight(gap.endAt)),
                )
              : t.guidance.noGapYet
          }
        />
      </div>
    </div>
  );
}

function Tile({ label, value, sub, alert }: { label: string; value: string; sub: string; alert?: boolean }) {
  return (
    <div style={{ ...styles.tile, ...(alert ? styles.tileAlert : null) }}>
      <div style={styles.tileLabel}>{label}</div>
      <div className="bh-num" style={{ ...styles.tileValue, color: alert ? "var(--bh-attn)" : "var(--bh-ink)" }}>
        {value}
      </div>
      <div style={styles.tileSub}>{sub}</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { margin: "0 14px 16px" },
  label: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    padding: "0 4px",
    marginBottom: 10,
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  tile: {
    background: "var(--bh-surface)",
    borderRadius: 16,
    padding: "12px 14px",
    border: "1px solid transparent",
  },
  tileAlert: {
    background: "var(--bh-attn-soft)",
    border: "1px solid color-mix(in oklch, var(--bh-attn) 35%, transparent)",
  },
  tileLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
  },
  tileValue: { fontSize: 17, fontWeight: 600, marginTop: 3 },
  tileSub: { fontSize: 12, color: "var(--bh-ink-3)", marginTop: 3 },
};
